import { useMemo } from 'react';
import type { Product } from '@/api/products';
import {
  transformProduct,
  type UIProductMockupTemplate,
} from '@/lib/product-transformer';
import { isEmbroideryProduct } from '@/lib/decoration-method';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { withEffectiveMethod } from './direct-sale-methods';
import { useDirectSaleMethod } from './useDirectSaleMethod';

/**
 * What a mockup of this product needs besides the design: the template of a
 * view, the garment colour and whether it is embroidered. One source for the
 * DOM preview (MockupRenderer) and the composed image (mockupComposite), so
 * the two cannot drift apart. Embroidery follows the decoration method the
 * customer chose, not only the product's default.
 */
export function useDirectSaleMockupInput(
  product: Product,
  positionSlug: string | null,
  colorIdOverride?: string
) {
  const selectedColorId = useDirectSaleStore((s) => s.colorId);
  const colorId = colorIdOverride ?? selectedColorId;

  const { effectiveId } = useDirectSaleMethod(product);
  const ui = useMemo(
    () => transformProduct(withEffectiveMethod(product, effectiveId)),
    [product, effectiveId]
  );
  const template = useMemo(() => {
    let found: UIProductMockupTemplate | null = null;
    for (const candidate of ui.mockupTemplates) {
      if (candidate.positionKey !== positionSlug) continue;
      // Same pick as the position switcher: the default template wins.
      if (!found || candidate.isDefault) found = candidate;
    }
    return found;
  }, [ui, positionSlug]);

  const color = ui.colors.find((c) => c.id === colorId) ?? ui.colors[0] ?? null;

  return {
    template,
    backgroundColor: color?.hex ?? '#FFFFFF',
    isHeathered: color?.isHeathered ?? false,
    isEmbroidery: isEmbroideryProduct(ui),
  };
}
